import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import {
  cancelMobileNotificationCampaign,
  deleteMobileNotificationCampaign,
  duplicateMobileNotificationCampaign,
  listMobileNotificationCampaigns,
  mapLaravelError,
} from '../../services/mobileNotificationCampaignsApi';
import MobileNotificationStatusChip from './components/MobileNotificationStatusChip';

const STATUS_FILTERS = ['', 'draft', 'scheduled', 'sending', 'sent', 'cancelled', 'failed'];

export default function MobileNotificationCampaignsListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 15, total: 0 });
  const [filters, setFilters] = useState({
    status: '',
    q: '',
    date_from: '',
    date_to: '',
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await listMobileNotificationCampaigns({
        ...filters,
        page,
        per_page: perPage,
      });
      setRows(Array.isArray(payload?.data) ? payload.data : []);
      setMeta(payload?.meta || { current_page: page, per_page: perPage, total: 0 });
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  const canCancel = (campaign) => String(campaign?.status || '').toLowerCase() === 'scheduled';
  const pageIndex = Math.max(0, Number(page || 1) - 1);

  const summary = useMemo(() => rows.reduce((acc, campaign) => {
    const status = String(campaign?.status || '').toLowerCase();
    if (Object.prototype.hasOwnProperty.call(acc, status)) {
      acc[status] += 1;
    }
    return acc;
  }, {
    draft: 0,
    scheduled: 0,
    sending: 0,
    sent: 0,
    cancelled: 0,
    failed: 0,
  }), [rows]);

  const resetFilters = () => {
    setFilters({ status: '', q: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const handleCancel = async (campaign) => {
    try {
      await cancelMobileNotificationCampaign(campaign.id);
      await load();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDuplicate = async (campaign) => {
    try {
      const payload = await duplicateMobileNotificationCampaign(campaign.id);
      const nextId = payload?.campaign?.id || payload?.campaign?.data?.id;
      if (nextId) {
        navigate(`/admin/communications/mobile-notifications/${nextId}/edit`);
        return;
      }
      await load();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDelete = async (campaign) => {
    const confirmed = window.confirm(`Delete "${campaign.title || 'this campaign'}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteMobileNotificationCampaign(campaign.id);
      await load();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Mobile Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create tenant-scoped mobile-only inbox and push campaigns for employees.
            </Typography>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => navigate('/admin/communications/mobile-notifications/new')}
          >
            Create Campaign
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Alert severity="info">
          These campaigns are visible on mobile only. Web notification center and navbar ignore them by design.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Campaign snapshot (current page)</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Draft: ${summary.draft}`} size="small" />
              <Chip label={`Scheduled: ${summary.scheduled}`} size="small" color="info" />
              <Chip label={`Sending: ${summary.sending}`} size="small" color="warning" />
              <Chip label={`Sent: ${summary.sent}`} size="small" color="success" />
              <Chip label={`Cancelled: ${summary.cancelled}`} size="small" />
              <Chip label={`Failed: ${summary.failed}`} size="small" color="error" />
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              size="small"
              select
              label="Status"
              value={filters.status}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, status: event.target.value }));
                setPage(1);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_FILTERS.filter(Boolean).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Search"
              value={filters.q}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, q: event.target.value }));
                setPage(1);
              }}
              sx={{ minWidth: 220, flex: 1 }}
            />

            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, date_from: event.target.value }));
                setPage(1);
              }}
            />

            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, date_to: event.target.value }));
                setPage(1);
              }}
            />

            <Button onClick={resetFilters}>Reset</Button>
          </Stack>
        </Paper>

        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No mobile notification campaigns found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 340 }}>
                          {row.message}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <MobileNotificationStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>{row.audience_summary}</TableCell>
                    <TableCell>{Number(row.recipient_snapshot_count || 0)}</TableCell>
                    <TableCell>{row.send_mode || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => navigate(`/admin/communications/mobile-notifications/${row.id}`)}>
                        <VisibilityOutlinedIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDuplicate(row)} disabled={!row.can_duplicate}>
                        <ContentCopyOutlinedIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => navigate(`/admin/communications/mobile-notifications/${row.id}/edit`)}
                        disabled={row.is_immutable}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Edit</Typography>
                      </IconButton>
                      <IconButton onClick={() => handleCancel(row)} disabled={!canCancel(row)}>
                        <CancelOutlinedIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(row)} disabled={!row.can_delete} color="error">
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={Number(meta.total || 0)}
            page={pageIndex}
            onPageChange={(_, nextPage) => setPage(nextPage + 1)}
            rowsPerPage={Number(perPage || 15)}
            onRowsPerPageChange={(event) => {
              const nextPerPage = Number(event.target.value || 15);
              setPerPage(nextPerPage);
              setPage(1);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </Paper>
      </Stack>
    </Box>
  );
}
